"use client";

import { useState } from "react";
import { useTranslations } from "@i18n/provider";

/**
 * Apple 5.1.2(i)'s explicit permission, asked where the transmitting happens
 * (CMP-01, CMP-02, CMP-03).
 *
 * WHERE IT APPEARS, AND WHY NOT EARLIER. CMP-01 says after account creation and
 * before the first tutoring turn. It renders INSIDE the tutor panel, in place
 * of the conversation, rather than as a full-page block on every route: the
 * permission is for sending messages to a third party, so the moment to ask is
 * when the learner opens the thing that sends them. A modal over the whole app
 * would ask people who are reading a lesson and transmitting nothing.
 *
 * TWO CONTROLS, SEPARATELY ACTIONABLE (CMP-03). The required consent covers
 * processing; the optional one covers durable tutor memory. They are two
 * checkboxes bound to two states, and the submit is disabled until the required
 * one is ticked - the same shape CMP-41's purchase consents already use. One
 * combined box would be a single permission standing for two different things.
 *
 * NEITHER IS PRE-TICKED. A pre-ticked box is not an affirmative action, and the
 * optional one being pre-ticked would be worse than the required one: it
 * collects an answer nobody gave to a question they could have said no to.
 *
 * WHAT THE COPY CLAIMS AND WHAT IT DOES NOT. It names OpenAI and Anthropic, and
 * attributes the no-training statement to THEM rather than presenting it as a
 * control Convo exercises. It makes no claim about a processing country: no
 * guideline asks for one and nothing in the provider configuration pins a
 * region, so the claim would be false. That is the CMP-02 amendment of
 * 2026-09-06.
 */
export function AiConsentPanel({ onConsented }: { onConsented: () => void }) {
  const { t } = useTranslations();
  const [accepted, setAccepted] = useState(false);
  const [memory, setMemory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    if (!accepted) {
      setError(t("aiConsent.needsRequired"));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/consent/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The VERSION is not sent. A client that could name it could claim
        // consent to a disclosure it never showed.
        body: JSON.stringify({ accepted: true, tutorMemory: memory }),
      });
      if (!res.ok) {
        setError(t("aiConsent.error"));
        return;
      }
      onConsented();
    } catch {
      setError(t("aiConsent.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="ai-consent-panel" className="flex flex-col gap-3 p-4 text-sm">
      <h2 className="font-display text-base font-bold text-ink">{t("aiConsent.title")}</h2>
      <p className="text-ink-body">{t("aiConsent.recipients")}</p>
      <p className="text-ink-muted">{t("aiConsent.noTraining")}</p>

      <label className="flex items-start gap-2 text-ink-body">
        <input
          type="checkbox"
          data-testid="ai-consent-required"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-1"
        />
        <span>{t("aiConsent.required")}</span>
      </label>

      <label className="flex items-start gap-2 text-ink-body">
        <input
          type="checkbox"
          data-testid="ai-consent-memory"
          checked={memory}
          onChange={(e) => setMemory(e.target.checked)}
          className="mt-1"
        />
        <span>{t("aiConsent.memory")}</span>
      </label>

      {error ? (
        <p role="alert" className="text-writing-fg">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        data-testid="ai-consent-submit"
        onClick={() => void submit()}
        // The guard is repeated in `submit`, so a disabled attribute alone is
        // not the only thing between a half-consented learner and a request.
        disabled={!accepted || busy}
        className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t("aiConsent.accept")}
      </button>
    </div>
  );
}
